import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatCurrency, formatDate } from '../../utils/format';
import { amountOf, peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge, SummaryNote
} from '../../components/ui';
import type { BudgetDocument, Expense } from '../../types';
import { expenseSummary, NEAR_BUDGET_PCT } from './analytics';
import type { BudgetLineVariance } from './budgetLines';

export interface ExpenseSummaryProps {
  expenses: Expense[];
  budget?: BudgetDocument | null;
  /** Voided count from the ledger's counts, when `expenses` holds only live rows. */
  voided?: number;
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Expenses module's Summary tab: tiles, then report panels and watch-lists. */
const ExpenseSummaryView = ({ expenses, budget, voided, loading, isDarkMode }: ExpenseSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => expenseSummary(expenses, budget), [expenses, budget]);
  const monthPeak = peakOf(s.months, (r) => r.total);
  const payeePeak = peakOf(s.topPayees, (r) => r.total);
  const money = (v: number) => formatCurrency(v);
  const usedPct = s.budgetTotal > 0 ? percent(s.budgetSpent, s.budgetTotal) : null;
  const watch = [...s.over, ...s.near];
  const voidCount = voided ?? s.voided;

  const lineColumns = [
    { key: 'item', header: 'Budget Line', render: (r: BudgetLineVariance) => (
      <span><strong>{r.item}</strong><span style={{ display: 'block', fontSize: '11px', color: t.textFaint }}>{r.head}</span></span>
    ) },
    { key: 'bar', header: 'Used', render: (r: BudgetLineVariance) => (
      <BarCell value={percent(r.actual, r.budget)} max={100} showPercent color={r.actual > r.budget ? t.danger : t.warning} isDarkMode={isDarkMode} />
    ) },
    { key: 'budget', header: 'Budget', align: 'right' as const, nowrap: true, render: (r: BudgetLineVariance) => money(r.budget) },
    { key: 'actual', header: 'Spent', align: 'right' as const, nowrap: true, render: (r: BudgetLineVariance) => <strong>{money(r.actual)}</strong> },
    { key: 'left', header: 'Left / Over', align: 'right' as const, nowrap: true, render: (r: BudgetLineVariance) => (
      r.actual > r.budget
        ? <Badge tone="error">over {money(r.actual - r.budget)}</Badge>
        : <span style={{ color: t.warning, fontWeight: 'bold' }}>{money(r.budget - r.actual)}</span>
    ) }
  ];

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="This Month" value={money(s.thisMonth)} color={t.expense} isDarkMode={isDarkMode} />
        <KpiCard label="Last Month" value={money(s.lastMonth)} color={t.textFaint} isDarkMode={isDarkMode} />
        <KpiCard label={`FY ${s.fy} (${s.fyCount})`} value={money(s.fyTotal)} color={t.expense} isDarkMode={isDarkMode} />
        <KpiCard label="Monthly Average (FY)" value={money(s.monthlyAvg)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="FY Budget" value={s.budgetTotal ? money(s.budgetTotal) : '—'} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Budget Used" value={usedPct === null ? '—' : `${usedPct}%`} color={usedPct !== null && usedPct > 100 ? t.danger : t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Lines Over Budget" value={String(s.over.length)} color={s.over.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label={`Lines Near (≥${NEAR_BUDGET_PCT}%)`} value={String(s.near.length)} color={s.near.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Not in Budget (FY)" value={money(s.unbudgeted)} color={s.unbudgeted ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Missing Paperwork" value={String(s.gaps.length)} color={s.gaps.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label={`All Time (${s.liveCount})`} value={money(s.allTime)} color={t.expense} isDarkMode={isDarkMode} />
        <KpiCard label="Voided" value={String(voidCount)} color={voidCount ? t.danger : t.textFaint} isDarkMode={isDarkMode} />
      </KpiGrid>

      <TablePanel
        title="Spend by Month"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="420px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={monthPeak} color={t.expense} isDarkMode={isDarkMode} /> },
          { key: 'count', header: 'Entries', align: 'right' },
          { key: 'total', header: 'Spent', align: 'right', nowrap: true, render: (r) => <strong>{money(r.total)}</strong> }
        ]}
        rows={s.months}
        rowKey="month"
        emptyMessage="No expenses recorded yet."
        below={<TotalRow label="Last 12 months" value={money(s.months.reduce((n, m) => n + m.total, 0))} color={t.expense} isDarkMode={isDarkMode} />}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title={`By Category — FY ${s.fy}`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Category', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.total, s.fyTotal)} max={100} showPercent color={t.expense} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Entries', align: 'right' },
            { key: 'total', header: 'Spent', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.byCategory}
          rowKey="name"
          emptyMessage="No expenses this financial year."
          below={s.byCategory.length ? <TotalRow label="Total spent" value={money(s.fyTotal)} color={t.expense} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title={`By Payment Mode — FY ${s.fy}`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Mode', render: (r) => <Badge tone="neutral">{r.name}</Badge> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.total, s.fyTotal)} max={100} showPercent color={t.info} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Payments', align: 'right' },
            { key: 'total', header: 'Paid', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.byMode}
          rowKey="name"
          emptyMessage="No expenses this financial year."
          below={s.byMode.length ? (
            <>
              <TotalRow label="Total paid" value={money(s.fyTotal)} color={t.expense} isDarkMode={isDarkMode} />
              <SummaryNote isDarkMode={isDarkMode}>Split payments are counted under each mode they were paid from.</SummaryNote>
            </>
          ) : null}
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title={`Top Payees — FY ${s.fy}`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Paid To', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={payeePeak} color={t.expense} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Bills', align: 'right' },
            { key: 'total', header: 'Paid', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.topPayees}
          rowKey="name"
          emptyMessage="No payees recorded this financial year."
        />
        <TablePanel
          title={`Budget Lines to Watch — FY ${s.fy}`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={lineColumns}
          rows={watch}
          rowKey={(r) => `${r.head}|${r.item}`}
          emptyMessage={s.budgetTotal ? `Every budget line is under ${NEAR_BUDGET_PCT}% of its allowance.` : `No Annual Budget for ${s.fy} yet.`}
        />
      </SummaryColumns>

      <TablePanel
        title="Expenses Missing Paperwork"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="620px"
        columns={[
          { key: 'internalId', header: 'ID', nowrap: true, render: (r) => <strong>{r.expense.internalId || '-'}</strong> },
          { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.expense.date) },
          { key: 'expenseName', header: 'Expense', render: (r) => (
            <span>{r.expense.expenseName || '-'}<span style={{ display: 'block', fontSize: '11px', color: t.textFaint }}>{r.expense.category}</span></span>
          ) },
          { key: 'paymentMode', header: 'Mode', nowrap: true, render: (r) => r.expense.paymentMode || '-' },
          { key: 'missing', header: 'Missing', render: (r) => (
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {r.missing.map((m) => <Badge key={m} tone="warning">{m}</Badge>)}
            </span>
          ) },
          { key: 'amount', header: 'Amount', align: 'right', nowrap: true, render: (r) => <strong>{money(amountOf(r.expense.amount))}</strong> }
        ]}
        rows={s.gaps}
        rowKey={(r) => String(r.expense.id ?? `${r.expense.internalId}|${r.expense.date}`)}
        emptyMessage="Every expense has its bill number, payment reference and approver."
      />
    </>
  );
};

export default ExpenseSummaryView;
